import { motion } from "framer-motion";
import { products } from "@/data/products";

/**
 * FeaturedProducts — asymmetric editorial grid.
 * Cards either display the real cosmic product photograph or a
 * pan-African cosmic gradient with typography + product motto.
 */

function CosmicCard({ product, index }) {
  const {
    name,
    subtitle,
    price,
    tag,
    image,
    gradient,
    accent,
    accentLabel,
    quote,
  } = product;

  return (
    <div className="relative overflow-hidden border border-[#362b28] bg-black">
      <div className={`relative w-full aspect-[3/4]`}>
        {image ? (
          <img
            src={image}
            alt={`${name} — ${subtitle}`}
            className="product-image absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <>
            {/* Cosmic gradient backdrop */}
            <div
              className="absolute inset-0 product-image"
              style={{ background: gradient }}
            />
            {/* Cosmic dust particles */}
            <div className="absolute inset-0 opacity-30 mix-blend-screen bg-[radial-gradient(circle_at_30%_20%,rgba(198,40,40,0.6),transparent_45%),radial-gradient(circle_at_75%_75%,rgba(44,138,62,0.5),transparent_50%),radial-gradient(circle_at_60%_40%,rgba(194,139,70,0.35),transparent_55%)]" />
            {/* Subtle grain */}
            <div className="absolute inset-0 opacity-30 mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%22.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%22.5%22/></svg>')]" />

            {/* Centered ESA-style stamp */}
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <p
                className="text-[10px] tracking-[0.42em] uppercase mb-4"
                style={{ color: accent }}
              >
                {accentLabel}
              </p>
              <div
                className="w-14 h-14 rounded-full border flex items-center justify-center mb-6"
                style={{ borderColor: accent }}
              >
                <span
                  className="font-serif italic text-[22px]"
                  style={{ color: accent }}
                >
                  E×A
                </span>
              </div>
              <p className="font-serif italic text-[#e8dcc4] text-[16px] md:text-[18px] leading-snug max-w-[22ch]">
                “{quote}”
              </p>
              <div className="mt-6 h-[1px] w-10" style={{ background: accent }} />
              <p className="mt-6 text-[10px] tracking-[0.34em] uppercase text-[#c8b99c]">
                Genesis 2:7
              </p>
            </div>
          </>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0806] via-transparent to-transparent opacity-70" />

        <div className="absolute top-4 left-4">
          <span
            className="inline-block px-3 py-1 bg-[#0a0806]/80 backdrop-blur-sm border text-[10px] tracking-[0.22em] uppercase font-semibold"
            style={{ borderColor: `${accent}66`, color: accent }}
          >
            {tag}
          </span>
        </div>

        <div className="absolute bottom-4 right-4 product-shop">
          <span
            className="inline-flex items-center gap-2 text-[#f5ebd8] px-4 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold"
            style={{ background: accent }}
          >
            Shop Now
            <span aria-hidden>→</span>
          </span>
        </div>

        <span
          className="absolute top-4 right-4 font-serif italic text-[16px]"
          style={{ color: accent }}
        >
          0{index + 1}
        </span>
      </div>
    </div>
  );
}

export default function FeaturedProducts() {
  return (
    <section
      id="shop"
      className="relative bg-[#1a1412] py-28 md:py-40 border-t border-[#362b28]"
      data-testid="featured-products-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow mb-6 flex items-center gap-4">
              <span className="w-10 h-[1px] bg-[#c28b46]" />
              The Collection · <span className="text-[#c62828]">Mind.</span>{" "}
              <span className="text-[#c28b46]">Body.</span>{" "}
              <span className="text-[#2c8a3e]">Earth.</span>
            </p>
            <h2 className="font-serif text-[44px] md:text-[76px] leading-[0.95] tracking-tight text-[#e8dcc4]">
              Seven pieces. <br />
              <em className="italic text-[#c28b46]">One archetype.</em>
            </h2>
          </div>
          <div className="hidden md:flex col-span-12 md:col-span-4 md:col-start-9 items-end justify-end">
            <p className="text-[12px] tracking-[0.24em] uppercase text-[#a89b8c] text-right">
              (01 — 07) · Live on Etsy <br />
              <span className="text-[#c28b46]">Wear Your Archetype.</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {products.map((p, i) => {
            const offset = i % 2 === 1 ? "md:mt-20" : "";
            return (
              <motion.a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.9,
                  delay: 0.06 * i,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className={`product-card group col-span-12 sm:col-span-6 lg:col-span-4 ${offset} block`}
                data-testid={`product-card-${p.id}`}
              >
                <CosmicCard product={p} index={i} />

                <div className="mt-5 flex items-start justify-between gap-6">
                  <div>
                    <h3 className="font-serif text-[20px] md:text-[22px] leading-tight text-[#e8dcc4]">
                      {p.name}
                    </h3>
                    <p className="mt-1 text-[13px] text-[#a89b8c] tracking-wide">
                      {p.subtitle}
                    </p>
                  </div>
                  <p
                    className="font-serif text-[18px] md:text-[20px] shrink-0"
                    style={{ color: p.accent || "#c28b46" }}
                  >
                    {p.price}
                  </p>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
